/**
 * Counts a position held, rather than a movement repeated.
 *
 * The third shape of drill in this app, and the first that is not a rep at all.
 * `metrics/revolutions.js` counts a signal that goes round;
 * `exercises/oscillation.js` counts one that leaves a resting value and comes
 * back. A handstand does neither: the athlete arrives somewhere and stays, and
 * what is worth counting is the time.
 *
 * Two different questions are being asked of each frame, and keeping them apart
 * is what makes this honest:
 *
 * - **`holding` — is the hold under way at all?** For the handstand that is
 *   simply being upside down. It is what opens and closes a hold, so it is the
 *   only thing the counter enforces.
 * - **`inLine` — is the posture right *now*?** That is what the athlete hears
 *   about and what the seconds are counted against, and it is deliberately NOT
 *   what opens the hold. If a hold could only exist while the posture was good,
 *   every criterion scored afterwards would pass by construction — the V-sit
 *   taught this app that lesson (see `docs/exercises.md`) and a hold is where it
 *   is easiest to get wrong, because the position and the quality of it look
 *   like the same measurement.
 *
 * **The state is settled before it is announced.** `inLine` is a threshold on a
 * continuous quantity, so at the edge it flickers from frame to frame, and every
 * flicker here is a sound in the athlete's ear. `settleMs` is the same idea as
 * the oscillation counter's two thresholds: a change has to persist before it
 * counts as a change.
 *
 * **The seconds counted are the seconds in line.** Not the time upside down: the
 * thing being trained is the shape, and a figure that grew while the athlete
 * sagged would pay for sagging, the same reason XP in this app is clean reps and
 * nothing else.
 */

export class HoldCounter {
  /**
   * @param options.holding Whether this frame is part of a hold at all.
   * @param options.inLine Whether the posture is right on this frame.
   * @param options.visibility Reads the visibility that gates the frame.
   * @param options.minVisibility Frames below this are refused outright, and a
   *   hold in progress ends rather than bridging the gap: the athlete has gone
   *   out of frame or come down, and either way what happened is not observed.
   * @param options.maxGapMs A longer gap between frames ends the hold too.
   * @param options.minHoldMs Shorter than this was a kick-up and a fall, not a
   *   hold, and it is dropped rather than banked as a very bad one.
   * @param options.settleMs How long a change in the posture has to persist
   *   before it is announced as one.
   */
  constructor({
    holding,
    inLine,
    visibility,
    minVisibility = 0.5,
    maxGapMs = 250,
    minHoldMs = 1000,
    settleMs = 300,
  }) {
    this.holding = holding;
    this.inLine = inLine;
    this.visibility = visibility;
    this.minVisibility = minVisibility;
    this.maxGapMs = maxGapMs;
    this.minHoldMs = minHoldMs;
    this.settleMs = settleMs;
    this.reset();
  }

  reset() {
    /** Holds completed and banked. */
    this.count = 0;
    /** "good" or "bad" while a hold is under way, null when there is none. */
    this.state = null;
    /** Seconds spent in line in the hold under way. The bar's headline figure. */
    this.seconds = 0;
    this.current = [];
    this.last = null;
    this.lastSide = null;
    this.lastRep = null;
    /** Milliseconds in line in the hold under way. */
    this.goodMs = 0;
    /** The previous frame's raw reading, which owns the interval after it. */
    this.lastReading = null;
    /** The reading waiting to become the state, and since when. */
    this.pending = null;
    this.pendingAt = null;
    /** Holds that ended before `minHoldMs`, for the record. */
    this.rejected = 0;
  }

  /**
   * Feeds one sample in. Returns the samples of a hold that this frame ended,
   * otherwise null — the same contract as the other two counters, so nothing
   * downstream has to know which kind of drill it is looking at.
   */
  push(sample, side) {
    this.lastSide = side;
    const gap = this.last != null && sample.t - this.last > this.maxGapMs;
    const seen = this.visibility(sample, side) >= this.minVisibility;
    this.last = sample.t;

    if (!seen || gap || !this.holding(sample, side)) {
      return this.closeHold();
    }

    const reading = this.inLine(sample, side) ? "good" : "bad";
    if (this.current.length === 0) {
      this.startHold();
    } else if (this.lastReading === "good") {
      // The interval between two frames belongs to the reading the first of
      // them gave. Summed off the RAW reading rather than the settled state,
      // because `settleMs` exists to keep the sound from chattering and must
      // not also move the arithmetic: the figure the bar shows and the figure
      // the ticket prints are then the same sum over the same frames, and
      // `scoreRep` can recompute it from the samples alone.
      this.goodMs += sample.t - this.current[this.current.length - 1].t;
    }
    this.lastReading = reading;
    this.current.push(sample);
    this.settle(reading, sample.t);
    this.seconds = this.goodMs / 1000;
    return null;
  }

  startHold() {
    this.goodMs = 0;
    this.seconds = 0;
    this.lastReading = null;
    this.pending = null;
    this.pendingAt = null;
    // A hold opens as "bad" rather than as nothing: the athlete has just kicked
    // up and is not in line yet, so the first settled reading being "good" is a
    // change, and a change is what makes the sound.
    this.state = "bad";
  }

  /** Applies `settleMs` of hysteresis to a raw reading. */
  settle(reading, t) {
    if (reading === this.state) {
      this.pending = null;
      this.pendingAt = null;
      return;
    }
    if (reading !== this.pending) {
      this.pending = reading;
      this.pendingAt = t;
      return;
    }
    if (t - this.pendingAt >= this.settleMs) {
      this.state = reading;
      this.pending = null;
      this.pendingAt = null;
    }
  }

  /**
   * Banks the hold in progress, if there was one worth banking.
   *
   * A hold that never reached `minHoldMs` is dropped rather than scored: the
   * athlete kicked up and came straight back down, and calling that a bad hold
   * would put a line on the ticket for something nobody attempted to hold.
   */
  closeHold() {
    if (this.current.length === 0) {
      this.state = null;
      return null;
    }
    const closed = this.current;
    const heldMs = closed[closed.length - 1].t - closed[0].t;
    this.current = [];
    this.state = null;
    this.seconds = 0;
    this.pending = null;
    this.pendingAt = null;
    if (heldMs < this.minHoldMs) {
      this.rejected += 1;
      this.goodMs = 0;
      return null;
    }
    this.count += 1;
    this.lastRep = closed;
    this.goodMs = 0;
    return closed;
  }

  /** The side the last sample was read on, to match the other counters. */
  get side() {
    return this.lastSide;
  }

  /**
   * Closes a hold still under way at the end of a set.
   *
   * Unlike the other two counters this is the normal case rather than the edge
   * one: a hold ends because the athlete came down, and coming down and pressing
   * Stop are the same moment. Without this the only hold of a set would be the
   * one that never made it to the ticket.
   */
  flush() {
    return this.closeHold();
  }

  /** Abandons the hold in progress without banking it. */
  break() {
    this.current = [];
    this.state = null;
    this.seconds = 0;
    this.goodMs = 0;
    this.last = null;
    this.pending = null;
    this.pendingAt = null;
  }

  /**
   * How far through the hold, 0 to 1, against `minHoldMs`.
   *
   * A hold has no natural end to measure against the way a rep does, so the bar
   * fills to the point the hold counts at all and stays full after it. That is
   * the one moment in a hold worth marking.
   */
  get progress() {
    if (this.current.length === 0 || this.minHoldMs <= 0) {
      return 0;
    }
    const heldMs = this.current[this.current.length - 1].t - this.current[0].t;
    return Math.min(1, heldMs / this.minHoldMs);
  }
}
